import { useState, useEffect, useCallback } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Loader2,
  RefreshCw,
  Server,
  Wrench,
  Database,
  ShieldAlert,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { isPlatformOrg, isSuperAdmin } from "@/lib/roles";
import {
  automationApi,
  MCPServerSummary,
  MCPServerDetail,
} from "@/services/api/automation";
import { toast } from "sonner";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { Pagination } from "@/components/ui/pagination";
import { useSortableData } from "@/hooks/useSortableData";
import { useClientPaginatedData } from "@/hooks/usePaginatedData";

export const Route = createFileRoute("/portal/automation/mcp")({
  component: MCPServers,
});

function MCPServers() {
  const { token, session } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const [servers, setServers] = useState<MCPServerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedServer, setSelectedServer] = useState<MCPServerDetail | null>(
    null,
  );
  const [detailOpen, setDetailOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [checkingHealth, setCheckingHealth] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const loadServers = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await automationApi.mcp.listServers(token);
      setServers(response.servers);
    } catch (error) {
      console.error("Failed to load MCP servers:", error);
      toast.error("Failed to load MCP servers");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  // Sort + paginate servers
  const { sortedData: sortedServers, requestSort, getSortDirection } =
    useSortableData(servers, {
      initialSort: { key: "name", direction: "asc" },
    });

  const {
    paginatedData,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
  } = useClientPaginatedData({
    data: sortedServers as unknown as Record<string, unknown>[],
    itemsPerPage: 25,
    searchFields: ["name", "transport", "url"],
    searchTerm,
  });
  const displayServers = paginatedData as unknown as MCPServerSummary[];

  // MCP Servers requires SuperAdmin + platform org selected
  if (!isSuperAdmin(session) || !isPlatformOrg(selectedOrganisation)) {
    return (
      <div className="space-y-6">
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="py-12 text-center">
            <ShieldAlert className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Access Restricted</h3>
            <p className="text-muted-foreground mt-2">
              MCP Server management is available to platform administrators on the platform organisation only.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleCheckHealth = async (serverName: string) => {
    if (!token) return;
    try {
      setCheckingHealth(serverName);
      const response = await automationApi.mcp.checkHealth(token, serverName);

      setServers((prev) =>
        prev.map((s) =>
          s.name === serverName
            ? {
                ...s,
                status: response.status,
                last_health_check: new Date().toISOString(),
              }
            : s,
        ),
      );

      toast.success(
        response.status === "healthy" ? "Server Healthy" : "Server Unhealthy",
        {
          description: response.latency_ms
            ? `Latency: ${response.latency_ms}ms`
            : undefined,
        },
      );
    } catch (error) {
      console.error("Failed to check health:", error);
      toast.error("Health Check Failed");
    } finally {
      setCheckingHealth(null);
    }
  };

  const handleViewDetail = async (serverName: string) => {
    if (!token) return;
    try {
      setLoadingDetail(true);
      setDetailOpen(true);
      const response = await automationApi.mcp.getServer(token, serverName);
      setSelectedServer(response);
    } catch (error) {
      console.error("Failed to load server detail:", error);
      toast.error("Failed to load server details");
    } finally {
      setLoadingDetail(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "healthy":
        return <Badge variant="success">Healthy</Badge>;
      case "unhealthy":
        return (
          <Badge variant="destructive">
            Unhealthy
          </Badge>
        );
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">MCP Servers</h1>
          <p className="mt-2 text-lg text-slate-600">
            Model Context Protocol servers for agent capabilities
          </p>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search servers..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Button variant="outline" size="sm" onClick={loadServers} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : displayServers.length === 0 ? (
          <div className="text-center py-12">
            <Server className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm ? "No servers match your search" : "No MCP Servers"}
            </h3>
            <p className="text-muted-foreground">
              {searchTerm ? "Try a different search term." : "No MCP servers are currently registered."}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead direction={getSortDirection("name")} onSort={() => requestSort("name")}>Name</SortableTableHead>
                <SortableTableHead direction={getSortDirection("transport")} onSort={() => requestSort("transport")}>Transport</SortableTableHead>
                <SortableTableHead direction={null} onSort={() => {}} disabled>URL</SortableTableHead>
                <SortableTableHead direction={getSortDirection("tools_count")} onSort={() => requestSort("tools_count")}>Tools</SortableTableHead>
                <SortableTableHead direction={getSortDirection("resources_count")} onSort={() => requestSort("resources_count")}>Resources</SortableTableHead>
                <SortableTableHead direction={getSortDirection("status")} onSort={() => requestSort("status")}>Status</SortableTableHead>
                <SortableTableHead direction={getSortDirection("last_health_check")} onSort={() => requestSort("last_health_check")}>Last Check</SortableTableHead>
                <SortableTableHead direction={null} onSort={() => {}} disabled className="w-[120px]">{" "}</SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayServers.map((server) => (
                <TableRow key={server.name} className="cursor-pointer hover:bg-muted/50" onClick={() => handleViewDetail(server.name)}>
                  <TableCell className="font-medium">{server.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{server.transport.toUpperCase()}</Badge>
                  </TableCell>
                  <TableCell className="max-w-[250px]">
                    <span className="text-sm font-mono text-muted-foreground truncate block">
                      {server.url || "—"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{server.tools_count}</TableCell>
                  <TableCell className="text-sm">{server.resources_count}</TableCell>
                  <TableCell>{getStatusBadge(server.status)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(server.last_health_check)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCheckHealth(server.name)}
                        disabled={checkingHealth === server.name}
                      >
                        {checkingHealth === server.name ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalCount={totalItems}
              pageSize={itemsPerPage}
              pageSizeOptions={[10, 25, 50]}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoPrev={currentPage > 1}
              canGoNext={currentPage < totalPages}
              onPageChange={setCurrentPage}
              onPageSizeChange={setItemsPerPage}
              onFirstPage={() => setCurrentPage(1)}
              onLastPage={() => setCurrentPage(totalPages)}
              onPrevPage={() => setCurrentPage(Math.max(1, currentPage - 1))}
              onNextPage={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            />
          </div>
        )}
      </div>

      {/* Server Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {loadingDetail
                ? "Loading..."
                : selectedServer?.name || "Server Details"}
            </DialogTitle>
            {selectedServer && !loadingDetail && (
              <DialogDescription>
                {selectedServer.transport.toUpperCase()} •{" "}
                {selectedServer.url || "No URL"} • Timeout:{" "}
                {selectedServer.timeout_seconds}s
              </DialogDescription>
            )}
          </DialogHeader>
          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : selectedServer ? (
            <>
              <div className="flex items-center gap-2 -mt-2">
                {getStatusBadge(selectedServer.status)}
              </div>

              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">
                      Last Health Check:
                    </span>
                    <span className="ml-2">
                      {formatDate(selectedServer.last_health_check)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">
                      Last Successful Call:
                    </span>
                    <span className="ml-2">
                      {formatDate(selectedServer.last_successful_call)}
                    </span>
                  </div>
                </div>

                <Accordion
                  type="multiple"
                  defaultValue={["tools", "resources"]}
                  className="space-y-2"
                >
                  {/* Tools */}
                  <AccordionItem
                    value="tools"
                    className="border rounded-lg px-4"
                  >
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Wrench className="h-4 w-4 text-blue-500" />
                        <span className="font-medium">Tools</span>
                        <Badge variant="secondary">
                          {selectedServer.tools.length}
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      {selectedServer.tools.length === 0 ? (
                        <p className="text-muted-foreground text-sm py-2">
                          No tools available
                        </p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead>Description</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedServer.tools.map((tool) => (
                              <TableRow key={tool.name}>
                                <TableCell className="font-mono text-sm">
                                  {tool.name}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                  {tool.description || "No description"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </AccordionContent>
                  </AccordionItem>

                  {/* Resources */}
                  <AccordionItem
                    value="resources"
                    className="border rounded-lg px-4"
                  >
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-2">
                        <Database className="h-4 w-4 text-green-500" />
                        <span className="font-medium">Resources</span>
                        <Badge variant="secondary">
                          {selectedServer.resources.length}
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      {selectedServer.resources.length === 0 ? (
                        <p className="text-muted-foreground text-sm py-2">
                          No resources available
                        </p>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead>URI Template</TableHead>
                              <TableHead>Description</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedServer.resources.map((resource) => (
                              <TableRow key={resource.uri_template}>
                                <TableCell className="font-medium">
                                  {resource.name}
                                </TableCell>
                                <TableCell className="font-mono text-sm text-muted-foreground">
                                  {resource.uri_template}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                  {resource.description || "No description"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
